import { useListAnalyses, useDeleteAnalysis } from '@workspace/api-client-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PlatformBadge } from '@/components/platform-badge';
import { StatusBadge } from '@/components/status-badge';
import { RecommendationBadge } from '@/components/recommendation-badge';
import { RiskBadge } from '@/components/risk-badge';
import { Spinner } from '@/components/ui/spinner';
import { Link } from 'wouter';
import { Trash2, Sparkles, TrendingUp } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export default function AnalysesPage() {
  const queryClient = useQueryClient();
  const { data: analysesData, isLoading } = useListAnalyses({ limit: 100 });
  const analyses = analysesData?.analyses;
  const deleteAnalysis = useDeleteAnalysis();

  const handleDelete = (id: number) => {
    deleteAnalysis.mutate(
      { id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['/api/analyses'] });
        },
      }
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 pb-24 lg:pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">My Analyses</h1>
          <p className="text-muted-foreground mt-1">
            {analysesData ? `${analysesData.total} total` : 'Loading...'}
          </p>
        </div>
        <Link href="/markets">
          <Button>
            <Sparkles className="mr-2 h-4 w-4" />
            New Analysis
          </Button>
        </Link>
      </div>

      {/* Analyses Table */}
      {isLoading ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Spinner size="lg" className="mx-auto" />
          </CardContent>
        </Card>
      ) : analyses && analyses.length > 0 ? (

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border">
                  <tr className="text-left text-sm text-muted-foreground">
                    <th className="p-4 font-medium">Date</th>
                    <th className="p-4 font-medium">Market</th>
                    <th className="p-4 font-medium">Platform</th>
                    <th className="p-4 font-medium">Rec</th>
                    <th className="p-4 font-medium">Confidence</th>
                    <th className="p-4 font-medium">Risk</th>
                    <th className="p-4 font-medium">Status</th>
                    <th className="p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {analyses!.map((analysis) => (
                    <tr key={analysis.id} className="border-b border-border last:border-0 hover:bg-muted/50">
                      <td className="p-4 text-sm text-muted-foreground">
                        {new Date(analysis.createdAt).toLocaleDateString()}
                      </td>
                      <td className="p-4">
                        <Link
                          href={`/markets/${analysis.marketId}`}
                          className="text-sm font-medium hover:text-primary line-clamp-2 max-w-md"
                        >
                          {analysis.marketQuestion}
                        </Link>
                      </td>
                      <td className="p-4">
                        <PlatformBadge platform={analysis.platform} />
                      </td>
                      <td className="p-4">
                        {analysis.status === 'complete' && analysis.recommendation ? (
                          <RecommendationBadge
                            recommendation={analysis.recommendation as 'YES' | 'NO'}
                            size="sm"
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-4">
                        {analysis.confidence ? (
                          <span className="text-sm font-semibold">
                            {Math.round(analysis.confidence)}%
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-4">
                        {analysis.riskLevel ? (
                          <RiskBadge risk={analysis.riskLevel as any} />
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-4">
                        <StatusBadge status={analysis.status} />
                      </td>
                      <td className="p-4">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                            >
                              <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Analysis</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete this analysis? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(analysis.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="h-16 w-16 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
              <Sparkles className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="font-semibold text-lg mb-2">No analyses yet</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Start analyzing markets to build your edge
            </p>
            <Link href="/markets">
              <Button>
                <TrendingUp className="mr-2 h-4 w-4" />
                Browse Markets
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
